const paths = require('../paths.json');

// Exported so App.js can ask "is this a custom domain?" without keeping a
// fourth hand-copied version of this list (the others are in
// common/custom_domain_media_middleware.py and fitfans/settings.py).
export const PLATFORM_HOSTNAMES = ['fitwith.io', 'withme.so', 'with-me.site', 'with-me.shop', 'with-me.store', 'localhost'];

// Custom domains serving the API from /api/* on their OWN hostname instead of
// from api.{domain}.
//
// Why this exists: api.{domain} costs a creator three of their four DNS
// records — the api CNAME itself, plus an ACM validation CNAME for its
// certificate, and that validation record cannot be avoided because API Gateway
// needs an ordinary ACM certificate and those validate only by DNS or email.
// Serving the API from the storefront's own hostname deletes all three at once
// and takes creator setup down towards a single CNAME.
//
// It is a LIST rather than a flag because one bundle is served to every custom
// domain. There is no per-domain build, so the only way to trial this on one
// domain is to name it here; every other domain keeps using api.{domain}.
//
// The CloudFront side is already live: an /api/* cache behaviour on the domains
// distribution forwards to the Fargate origin with caching disabled and the
// viewer's Host preserved. It is inert for any domain not listed here, because
// nothing else requests that path.
//
// Before adding a domain to this list, check a logged-in WRITE, not just a page
// load. Reads were verified identical across both paths, but going same-origin
// changes the Origin header Django sees from api.{domain} to {domain}.
//
// That Origin change turns out to be safe, which an earlier version of this
// comment got wrong by claiming CSRF_TRUSTED_ORIGINS was BASE_URL only — that
// is the local-dev settings shape on develop, not this branch, where it is a
// 143-entry list that already contains the storefront hostnames. And Django
// only consults that list when Origin and Host DISAGREE: the /api/* behaviour
// uses Managed-AllViewer, so Django sees the viewer's own Host, and
// ALLOWED_HOSTS is ['*']. A same-origin write therefore passes the CSRF origin
// check without needing an entry at all.
//
// The reason to still test a WRITE is different: ~541 @csrf_exempt decorators
// across influencers/, checkout/ and content/views.py mean a read proves
// nothing either way, and the session cookie host changes (see
// common/custom_domain_cookie_middleware.py).
const SAME_ORIGIN_API_HOSTNAMES = ['neuroscintilla.com'];

// Every custom domain that was live when the App.js chain was removed
// (2026-10-05) keeps calling api.{domain}: its customers' sessions are cookies
// on that api host, and moving them is a per-domain migration (logged-in write
// + logout test), not something to do to 69 storefronts at once. Mirrors
// LEGACY_API_SUBDOMAIN_HOSTNAMES in common/custom_domain_cookie_middleware.py,
// which must list the same domains. Frozen: domains only ever leave it.
//
// Any custom domain NOT listed here is same-origin by default. That is what lets
// a domain be onboarded with no deploy: it needs no api.{domain} DNS record,
// certificate or API Gateway domain, and no entry in any list. The backend side
// (the cookie middleware) must be live before this, see its module docstring.
const LEGACY_API_SUBDOMAIN_HOSTNAMES = [
    'areyoupraying.store', 'backtobalancenutrition.com',
    'beautifullifecoaches.com', 'becominghomospiritus.com', 'becomingsacred.me',
    'bettereracoaching.com', 'beyondcleaningcoaching.com', 'brilliantlybold.com',
    'brittandtoniart.com', 'calmskinsociety.com', 'certifiedaicopilot.com',
    'chiefrainmaker.co', 'christiannetwork.net', 'cleartoeternity.com',
    'confidentbritain.co.uk', 'cryptocosmopolitan.com', 'dennisisdigital.com',
    'echomyst.auction', 'elmagoclub.com', 'everyoneisaphysician.com',
    'fakealicious.org', 'fosteringembrace.com', 'getwithgibson.com',
    'globalcohorts.org', 'h2hplanninggroup.com', 'hindimitsandesh.online',
    'homebuyerscoaching.com', 'homewardbound.today', 'innatethriving.com',
    'inventorsmastermind.com', 'joe-warren.com', 'k2fit.online',
    'kellyraysbutchart.com', 'lawandahaneyfit.com', 'legacyxlearning.com',
    'lelitebests.com', 'lifegiving.online', 'lightwithlarry.com',
    'loveidentitycode.com', 'malayaneff.com', 'maltifloof.com',
    'marketingjester.de', 'marlenelive.com', 'maximizher.com', 'mediumonthego.com',
    'mindfulwithhelen.com', 'momentumwellness.club', 'mrmckos.com',
    'msholistic.com', 'mychatterton.com', 'mylast.love',
    'mytenniscoachingacademy.app', 'openroadenglish.com', 'passthebarnow.com',
    'polarchefben.com', 'quynn.com', 'redefineyou.fit',
    'selfmadeartistblueprint.com', 'skoolkilla.com', 'skoolkilla.de',
    'somaticoperatingsystem.online', 'soulwizdom.com', 'theoptionsdoctor.com',
    'theprofessionalblueprint.com', 'thewytchshaven.com', 'timetobloom.us',
    'toolboks.no', 'wealth-investors.com', 'with-me.site'
];

/**
 * True when the page is served from one of the platform hostnames, false on a
 * creator's custom domain (and when there is no window).
 */
export function isPlatformHost() {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname.replace(/^www\./, '');
    return PLATFORM_HOSTNAMES.includes(hostname);
}

/**
 * Returns the correct backend API URL.
 * On legacy custom domains (e.g. mylast.love), returns https://api.mylast.love/
 * On every other custom domain (incl. same-origin trial domains), returns the
 * page's own origin.
 * On platform domains (e.g. withme.so), returns the default paths.BACKEND_URL.
 */
export function getBackendUrl() {
    if (typeof window === 'undefined' || isPlatformHost()) return paths.BACKEND_URL;
    const hostname = window.location.hostname.replace(/^www\./, '');
    // window.location.origin, not the stripped hostname — the request has to go
    // back to the exact host the page was served from, www and all, or it is
    // not same-origin and the cookie does not travel.
    if (SAME_ORIGIN_API_HOSTNAMES.includes(hostname)) return `${window.location.origin}/`;
    if (LEGACY_API_SUBDOMAIN_HOSTNAMES.includes(hostname)) return `https://api.${hostname}/`;
    return `${window.location.origin}/`;
}

export default getBackendUrl;
